/**
 * DashboardNew — Phase 2B composer.
 *
 * Consumes useDashboardData() and derives per-section props ONLY from
 * authoritative sources documented in DASHBOARD_DATA_CONTRACT.md §1.
 * See DashboardVariant.js for the variant gate that decides whether this
 * page or the legacy `pages/Dashboard.js` renders on /dashboard.
 *
 * FI exclusion is enforced at every filter boundary
 * (Amendment 4 / 0.2b.5): commitments and goals are filtered by
 * `kind !== 'fi'` before being passed downstream.
 *
 * Timezone: delegated entirely to `lib/dateRange.js:isoLocal()` inside
 * useDashboardData(). No section-specific fallback here.
 *
 * FinancialBriefing and OneThingWorthDoing accept `briefing`/`action`
 * props that are `null` in Phase 2B. Phase 2C will supply `action`;
 * Phase 2D will supply `briefing`. Adding those hooks requires zero
 * component changes.
 */
import React, { useMemo, useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate, useSearchParams } from 'react-router-dom';
import usePageTitle from '@/hooks/usePageTitle';
import { useAuth } from '@/context/AuthContext';
import { useDashboardData } from '@/components/dashboard/useDashboardData';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
import { fetchGoalContributions } from '@/lib/dashboardApi';
import { supabase } from '@/lib/supabaseClient';
import { computeMonthlyRecaps } from '@/lib/insightsSignals';
import FinancialBriefingHero from '@/components/dashboard/FinancialBriefingHero';
import { DemoDataBanner } from '@/components/onboarding/DemoDataBanner';
import PositionCards       from '@/components/dashboard/PositionCards';
import NeedsYourAttention  from '@/components/dashboard/NeedsYourAttention';
import MoneyMovement       from '@/components/dashboard/MoneyMovement';
import CommittedMoney      from '@/components/dashboard/CommittedMoney';
import GoalProgress        from '@/components/dashboard/GoalProgress';
import MonthlyReflection   from '@/components/dashboard/MonthlyReflection';
import DetailDrawer        from '@/components/dashboard/DetailDrawer';
import ActivationChecklist from '@/components/dashboard/ActivationChecklist';
import RecordManualTxnCard from '@/components/dashboard/RecordManualTxnCard';
import DebtDueNudge        from '@/components/dashboard/DebtDueNudge';
// v3.7.15 Phase 84.32 Part 1 — new compact header + drawer + modal.
import DashboardHeader        from '@/components/dashboard/DashboardHeader';
import GettingStartedDrawer   from '@/components/dashboard/GettingStartedDrawer';
import RecordTransactionModal from '@/components/dashboard/RecordTransactionModal';
import ReportingCurrencyPrompt from '@/components/ReportingCurrencyPrompt';
import { setDemoEnabled } from '@/lib/dashboardDemo';
import {
  DEMO_TREND_12M, DEMO_COMMITTED_CATEGORIES, DEMO_COMMITTED_NOTE,
  DEMO_GOAL_MONTHLY_CONTRIBS,
  DEMO_PRIOR_MONTH_RECAP, DEMO_BIGGEST_OPPORTUNITY,
} from '@/lib/dashboardDemo';
import { Button } from '@/components/ui/button';

/** FI exclusion helper (Amendment 4 / 0.2b.5). */
const isFiGoal = (g) => (g?.kind || '').toLowerCase() === 'fi';

export default function DashboardNew() {
  usePageTitle('Overview');
  const navigate = useNavigate();
  const { profile, user, session, refreshProfile } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  const {
    loading, error, snapshot,
    breakdownThisMonth, breakdownLastMonth, breakdownCategories,
    reconciliation, recurringSuggestions,
    reflectionQuestion, latestReflection,
    trend6m, trend6mExcluded, baseCurrency,
    briefing, demoAttention, demoMode, setDemo, reload,
  } = useDashboardData();

  const greetingName = profile?.first_name || profile?.display_name || (user?.email || '').split('@')[0] || null;

  // v3.7.15 Phase 84.32 Part 1 — Getting Started drawer + Record
  // Transaction modal open state. Both are triggered from the compact
  // header controls; neither auto-opens on every login.
  const [checklistOpen, setChecklistOpen] = React.useState(false);
  const [recordOpen,    setRecordOpen]    = React.useState(false);
  const [dashAccounts,  setDashAccounts]  = React.useState([]);
  const [dashTxns,      setDashTxns]      = React.useState([]);

  // Pull light account + recent-txn lists to feed the Record modal.
  // Runs once on mount + when the modal opens to refresh chip inputs.
  React.useEffect(() => {
    if (!user?.id) return;
    let cancelled = false;
    (async () => {
      const [ares, tres] = await Promise.all([
        supabase.from('accounts').select('id, name, currency, type').eq('user_id', user.id).order('name'),
        supabase.from('transactions').select('description, amount, category, type, txn_date, account_id')
          .eq('user_id', user.id).order('txn_date', { ascending: false }).limit(200),
      ]);
      if (cancelled) return;
      const accs = ares?.data || [];
      // transactions have no currency column — it comes from the account.
      const ccyById = new Map(accs.map((a) => [a.id, a.currency]));
      setDashAccounts(accs);
      setDashTxns((tres?.data || []).map((t) => ({ ...t, currency: ccyById.get(t.account_id) || null })));
    })();
    return () => { cancelled = true; };
  }, [user?.id, recordOpen]);

  // -------------------------------------------------------------------------
  // Derive Position Cards inputs (all AVAILABLE per §4).
  // -------------------------------------------------------------------------
  const positionInputs = useMemo(() => {
    const spendVal = breakdownThisMonth?.total_expenses ?? null;
    const keptVal  = breakdownThisMonth?.total_surplus ?? null;
    // "Prior period" here is last30d in the current API (no proper
    // prior-month endpoint yet — documented in §3.5 as a low-priority
    // future addition). We display the delta only when the two windows
    // are both non-null and non-zero to avoid misleading swings.
    const spendPrev = breakdownLastMonth?.total_expenses ?? null;
    const keptPrev  = breakdownLastMonth?.total_surplus ?? null;
    const pct = (curr, prev) => {
      if (curr == null || prev == null || Math.abs(prev) < 0.01) return null;
      return ((curr - prev) / Math.abs(prev)) * 100;
    };

    // Filter FI goals out of the top-priority pick (0.2b.5).
    const goals = (snapshot?.goals?.items || []).filter((g) => !isFiGoal(g));
    const topId = snapshot?.goals?.top_priority_goal_id;
    const topGoal = goals.find((g) => g.id === topId) || goals[0] || null;

    return {
      spending:  { value: spendVal, delta_pct: pct(spendVal, spendPrev) },
      moneyKept: { value: keptVal,  delta_pct: pct(keptVal, keptPrev) },
      committed: {
        value: snapshot?.commitments?.total_monthly_out_base ?? null,
        count: (snapshot?.commitments?.items || []).filter((c) => c.active !== false && c.kind !== 'income').length,
      },
      goal: topGoal ? {
        progress_pct: topGoal.progress_pct,
        title:        topGoal.title,
        current_base: topGoal.current_amount_base,
        target_base:  topGoal.target_amount_base,
      } : null,
    };
  }, [snapshot, breakdownThisMonth, breakdownLastMonth]);

  // -------------------------------------------------------------------------
  // Financial Briefing key metrics (v1 factual placeholder).
  // -------------------------------------------------------------------------
  const briefingKeyMetrics = useMemo(() => {
    if (!snapshot) return [];
    const savings_rate = snapshot?.derived?.savings_rate_pct ?? null;
    const runway_months = snapshot?.derived?.runway_months ?? null;
    const surplus = breakdownThisMonth?.total_surplus ?? snapshot?.cashflow?.surplus_monthly ?? null;
    return [
      { key: 'money_kept',   label: 'Money kept',   value_base: surplus,       unit: 'currency', provenance: 'D' },
      { key: 'savings_rate', label: 'Savings rate', value_base: savings_rate,  unit: 'percent',  provenance: 'D' },
      { key: 'runway',       label: 'Runway',       value_base: runway_months, unit: 'months',   provenance: 'D' },
    ];
  }, [snapshot, breakdownThisMonth]);

  // -------------------------------------------------------------------------
  // Needs Your Attention rows (aggregate authoritative sources only).
  // Amendment 0.2b.3: NO unusual_expense row.
  // -------------------------------------------------------------------------
  const attentionItems = useMemo(() => {
    // In demo mode we surface the fixture rows instead of the derived ones,
    // so the Attention card also fills in with plausible entries.
    if (demoMode && Array.isArray(demoAttention)) return demoAttention;

    const items = [];

    // 1) payment_due: commitments with next_due in [today, today+14d].
    //    FI-linked commitments (should never exist since FI is a goal kind,
    //    not a commitment kind; defensive filter kept).
    const now = new Date();
    const in14 = new Date(now.getTime() + 14 * 24 * 3600 * 1000);
    for (const c of (snapshot?.commitments?.items || [])) {
      if (c.active === false) continue;
      if (c.kind === 'income') continue;
      if (!c.next_due) continue;
      const d = new Date(c.next_due);
      if (!isFinite(d.getTime()) || d > in14 || d < new Date(now.getFullYear(), now.getMonth(), now.getDate())) continue;
      items.push({
        id: `payment:${c.id}`,
        kind: 'payment_due',
        title: `${c.name || 'Payment'} due ${d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}`,
        sub_line: c.frequency ? `Recurring · ${c.frequency}` : undefined,
        severity: (d.getTime() - now.getTime()) < 3 * 24 * 3600 * 1000 ? 'high' : 'medium',
        drawer_type: 'attention-payment',
        linked_id: c.id,
        provenance: 'F',
        source_data: c,
      });
    }

    // 2) reconciliation: pending candidate count > 0.
    if ((reconciliation?.count || 0) > 0) {
      items.push({
        id: 'recon',
        kind: 'reconciliation',
        title: `${reconciliation.count} duplicate ${reconciliation.count === 1 ? 'candidate' : 'candidates'} to review`,
        sub_line: 'From your recent imports',
        severity: 'medium',
        drawer_type: null,
        cta: { label: 'Review', action: 'navigate', target: '/review-uncategorized' },
        provenance: 'D',
      });
    }

    // 3) recurring_suggestion: new streams detected (not amount-changed;
    //    that's in the §3.4 gap list).
    const openSuggestions = (recurringSuggestions || []).filter((s) => (s.status || 'pending') === 'pending');
    if (openSuggestions.length > 0) {
      items.push({
        id: 'recurring-sugg',
        kind: 'recurring_suggestion',
        title: `${openSuggestions.length} new recurring ${openSuggestions.length === 1 ? 'pattern' : 'patterns'} detected`,
        sub_line: 'Confirm or dismiss on the Recurring page',
        severity: 'low',
        drawer_type: 'attention-recurring',
        source_data: openSuggestions[0],
        provenance: 'D',
        cta: { label: 'Open recurring', action: 'navigate', target: '/recurring' },
      });
    }

    // 4) upload_reminder: derived from snapshot signals (no txns yet, or
    //    stale). We treat the absence of transactions as a lower-severity
    //    invitation.
    if (snapshot && snapshot?.flags?.has_transactions === false) {
      items.push({
        id: 'upload',
        kind: 'upload_reminder',
        title: 'Add or import your first transactions',
        sub_line: 'Your dashboard fills in as data lands',
        severity: 'low',
        drawer_type: null,
        cta: { label: 'Import', action: 'navigate', target: '/import' },
        provenance: 'F',
      });
    }

    return items;
  }, [snapshot, reconciliation, recurringSuggestions, demoMode, demoAttention]);

  // -------------------------------------------------------------------------
  // Detail drawer state — URL-driven so back button + share work.
  // -------------------------------------------------------------------------
  // -------------------------------------------------------------------------
  // Committed Money inputs (defined here so drawerData can also reference it).
  // Essential/Flexible split remains hidden per Amendment 6.
  // -------------------------------------------------------------------------
  const committedInputs = useMemo(() => {
    if (!snapshot) return {};
    const items = (snapshot?.commitments?.items || []).filter((c) => c.active !== false);
    return {
      totalMonthlyOutBase: snapshot?.commitments?.total_monthly_out_base ?? 0,
      activeCount: items.filter((c) => c.kind !== 'income').length,
      pctOfIncome: snapshot?.derived?.committed_outflow_pct_of_income ?? snapshot?.derived_committed_pct ?? null,
      items,
    };
  }, [snapshot]);

  // -------------------------------------------------------------------------
  // MonthlyReflection inputs (Phase 84.12 — bug fix).
  //
  // Historical bug: `priorMonthRecap` and `biggestOpportunity` were being
  // passed to `<MonthlyReflection>` as STRINGS derived from the briefing's
  // `rising`/`opportunity` update tabs, but the component's contract is
  // `{ title, body, provenance }` OBJECTS. A string has no `.title` /
  // `.body`, so both cards rendered with only the eyebrow visible and no
  // content — the "empty state" the user reported ("How June went is
  // empty and biggest opportunity for July is also empty").
  //
  // Also: the briefing's `rising` update reflects a WEEKLY signal (this
  // week's celebrate + progress folded together), not a monthly recap.
  // Using it for "How <PrevMonth> went" was semantically wrong anyway.
  //
  // Fix:
  //   • "How <PrevMonth> went"       — compute deterministically from the
  //                                    same `computeMonthlyRecaps()` that
  //                                    powers the Reflections tab, so both
  //                                    surfaces stay in sync (user asked
  //                                    for this explicitly). We fetch the
  //                                    last ~13 months of transactions
  //                                    (mirroring the Reflections fetch)
  //                                    and take the FIRST recap (most
  //                                    recent completed month).
  //   • "Biggest opportunity for <M>" — convert the briefing's
  //                                    `opportunity` update to the object
  //                                    shape so it actually renders.
  //                                    Falls back to `null` if the
  //                                    composer has no opportunity, which
  //                                    triggers the component's built-in
  //                                    question fallback.
  // -------------------------------------------------------------------------
  const [reflectionTxns, setReflectionTxns] = useState([]);

  // -------------------------------------------------------------------------
  // Phase 84.19.2 — Silent data-hygiene scan on Overview mount.
  //   • Fires POST /api/data-hygiene/scan which auto-collapses null-account
  //     duplicates (up to 200 rows per call) so the charts below always
  //     see a clean ledger.
  //   • Non-blocking, non-fatal. If it fails, dashboard renders normally.
  //   • Skipped for demo mode (no real data to heal).
  // -------------------------------------------------------------------------
  useEffect(() => {
    if (!user?.id || demoMode) return;
    (async () => {
      try {
        const token = session?.access_token;
        if (!token) return;
        await axios.post(
          `${BACKEND_URL}/api/data-hygiene/scan`,
          {},
          {
            headers: { Authorization: `Bearer ${token}` },
            timeout: 8000,
          },
        );
      } catch {
        // Silent — this is a background hygiene sweep. If the endpoint is
        // missing (older backend) or fails, the dashboard still works.
      }
    })();
    // Depend ONLY on user.id so we run once per session per user, not
    // every re-render.
  }, [user?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!user?.id || demoMode) return;
    let cancelled = false;
    (async () => {
      try {
        const start = new Date();
        start.setMonth(start.getMonth() - 13);
        start.setDate(1);
        const startISO = start.toISOString().slice(0, 10);
        const r = await supabase
          .from('transactions')
          .select('id, txn_date, amount, category, type, description')
          .gte('txn_date', startISO)
          // Newest-first — see Phase 84.9 rationale: guarantees the last
          // completed month is always in the returned slice even for
          // heavy-import users (>5000 txns in 13 months).
          .order('txn_date', { ascending: false })
          .limit(5000);
        if (!cancelled) setReflectionTxns(r.data || []);
      } catch (_) {
        /* non-fatal — MonthlyReflection falls back to its empty state */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user?.id, demoMode]);

  const priorMonthRecap = useMemo(() => {
    if (demoMode) return DEMO_PRIOR_MONTH_RECAP;
    if (!reflectionTxns || reflectionTxns.length === 0) return null;
    const recaps = computeMonthlyRecaps({ txns: reflectionTxns, baseCurrency });
    // Reflections tab returns recaps NEWEST-first with the current
    // month excluded — index 0 is the last completed month.
    const latest = recaps?.[0];
    if (!latest) return null;
    return {
      title: latest.headline,
      // Show the two most informative highlights joined; this mirrors what
      // a reader sees on the Reflections tab card without duplicating the
      // whole component.
      body: (latest.highlights || []).slice(0, 2).join(' '),
      provenance: 'D',
    };
  }, [reflectionTxns, baseCurrency, demoMode]);

  const biggestOpportunity = useMemo(() => {
    if (demoMode) return DEMO_BIGGEST_OPPORTUNITY;
    const updates = briefing?.updates || [];
    const opp = updates.find((u) => u.category === 'opportunity');
    if (!opp) return null;
    return {
      title: opp.headline || '',
      body: opp.sub_line || opp.body_line || '',
      provenance: opp.provenance || 'D',
    };
  }, [briefing, demoMode]);

  // Phase 86.C — the dashboard no longer composes its own spending breakdown.
  // It previously mapped /api/dashboard/breakdown `categories[]` and built its
  // own "what changed" sentence here, duplicating logic that also lived in
  // CashTrendsTab. The drawer now renders the shared <SpendingExplorer>, which
  // fetches from the single /api/spending/breakdown service.

  // Aggregate committed items into a category timeline for the
  // Committed Money drawer (Phase 83.4). Purely client-side — the
  // snapshot already includes every commitment.
  const committedCategoryBreakdown = useMemo(() => {
    if (demoMode) return DEMO_COMMITTED_CATEGORIES;
    const items = committedInputs.items || [];
    if (items.length === 0) return [];
    const by = new Map();
    for (const it of items) {
      if (!it || it.kind === 'income') continue;
      const cat = it.category || 'Other';
      // Roll everything up to a monthly equivalent so all rows are comparable.
      const freq = (it.frequency || 'monthly').toLowerCase();
      const divisor = { monthly: 1, fortnightly: 0.5, weekly: 0.25, quarterly: 3, yearly: 12, annual: 12 }[freq] || 1;
      const monthly = (Number(it.amount) || 0) / divisor;
      const prev = by.get(cat) || { name: cat, amount: 0, count: 0 };
      prev.amount += monthly;
      prev.count += 1;
      by.set(cat, prev);
    }
    return Array.from(by.values())
      .map((r) => ({ name: r.name, amount: Math.round(r.amount), count: r.count }))
      .sort((a, b) => b.amount - a.amount);
  }, [committedInputs, demoMode]);

  const drawerType = searchParams.get('drawer') || null;
  const drawerId   = searchParams.get('id') || null;
  const drawerData = useMemo(() => {
    if (!drawerType) return null;
    if (drawerType === 'briefing') return { key_metrics: briefingKeyMetrics };
    if (drawerType === 'committed') {
      return {
        total: committedInputs.totalMonthlyOutBase || 0,
        pctOfIncome: committedInputs.pctOfIncome,
        categories: committedCategoryBreakdown,
        note: demoMode ? DEMO_COMMITTED_NOTE : null,
      };
    }
    if (drawerType === 'goal') {
      const goals = (snapshot?.goals?.items || []).filter((g) => !isFiGoal(g));
      return goals.find((g) => g.id === drawerId) || goals[0] || null;
    }
    if (drawerType === 'attention-payment') {
      const row = attentionItems.find((i) => i.linked_id === drawerId && i.kind === 'payment_due');
      return row?.source_data || null;
    }
    if (drawerType === 'attention-recurring') {
      const row = attentionItems.find((i) => i.kind === 'recurring_suggestion');
      return row?.source_data || null;
    }
    if (drawerType === 'reflection') return reflectionQuestion || null;
    // Phase 84.7 — Rich NBA drawer. URL carries `?drawer=next-best-action&id=<update-id>`.
    // We look up the update in briefing.updates (hero tabs) OR briefing.secondary_updates
    // ("Also worth knowing") and hand its `next_best_action` payload plus the
    // headline for context to the drawer body. When the update lacks an NBA
    // (e.g. clicking a factual observation with no cta), we return null and
    // the drawer renders the "No next best action available" empty state.
    if (drawerType === 'next-best-action') {
      const all = [
        ...((briefing && briefing.updates) || []),
        ...((briefing && briefing.secondary_updates) || []),
      ];
      const upd = all.find((u) => u.id === drawerId) || null;
      if (!upd || !upd.next_best_action) return null;
      return { nba: upd.next_best_action, update_headline: upd.headline || null };
    }
    return null;
  }, [drawerType, drawerId, briefingKeyMetrics, snapshot, attentionItems, reflectionQuestion, demoMode, committedInputs, committedCategoryBreakdown, briefing]);

  const openDrawer = useCallback((type, id) => {
    const next = new URLSearchParams(searchParams);
    next.set('drawer', type);
    if (id) next.set('id', String(id)); else next.delete('id');
    setSearchParams(next, { replace: false });
  }, [searchParams, setSearchParams]);

  const closeDrawer = useCallback((next) => {
    if (next) return;
    const q = new URLSearchParams(searchParams);
    q.delete('drawer');
    q.delete('id');
    setSearchParams(q, { replace: true });
  }, [searchParams, setSearchParams]);

  // -------------------------------------------------------------------------
  // Goal contributions timeline (Phase 83.3/83.4). Fetched ON-DEMAND when
  // the user opens a goal drawer so the initial page load isn't slowed
  // down by an endpoint the average visit never triggers. Cached in local
  // state keyed by goal id so re-opening the same goal is instant.
  // -------------------------------------------------------------------------
  const [goalContribsByGoal, setGoalContribsByGoal] = useState({});
  useEffect(() => {
    if (demoMode) return; // demo mode already supplies fixture contributions
    if (drawerType !== 'goal') return;
    const goal = drawerData;
    if (!goal || !goal.id) return;
    if (goalContribsByGoal[goal.id]) return; // cached — no re-fetch
    let cancelled = false;
    (async () => {
      try {
        const res = await fetchGoalContributions(goal.id, 12);
        if (cancelled) return;
        // Backend returns `[{ym, label, amount, txn_count}]` — the drawer
        // expects `[{label, amount_base}]`. Adapt here so the drawer
        // contract stays clean.
        const rows = (res?.contributions || []).map((c) => ({
          label: c.label,
          amount_base: c.amount || 0,
        }));
        setGoalContribsByGoal((prev) => ({ ...prev, [goal.id]: rows }));
      } catch (_e) {
        // Non-fatal — the drawer simply hides the section when the array is empty.
        setGoalContribsByGoal((prev) => ({ ...prev, [goal.id]: [] }));
      }
    })();
    return () => { cancelled = true; };
  }, [drawerType, drawerData, demoMode, goalContribsByGoal]);
  const goalContribs = useMemo(() => {
    if (demoMode) return DEMO_GOAL_MONTHLY_CONTRIBS;
    const g = drawerData;
    if (!g || !g.id) return [];
    return goalContribsByGoal[g.id] || [];
  }, [demoMode, drawerData, goalContribsByGoal]);

  // -------------------------------------------------------------------------
  // Goal Progress inputs (FI filtered).
  // -------------------------------------------------------------------------
  const goalInputs = useMemo(() => {
    if (!snapshot) return { topGoal: null, onTrackCount: 0, offTrackCount: 0 };
    const goals = (snapshot?.goals?.items || []).filter((g) => !isFiGoal(g));
    const topId = snapshot?.goals?.top_priority_goal_id;
    const topGoal = goals.find((g) => g.id === topId) || goals[0] || null;
    // Recompute FI-excluded on/off-track counts from the filtered goals
    // (server-provided counts include FI goals we've dropped).
    const onTrackCount  = goals.filter((g) => g.on_track === true).length;
    const offTrackCount = goals.filter((g) => g.on_track === false).length;
    return { topGoal, onTrackCount, offTrackCount };
  }, [snapshot]);

  return (
    <div className="max-w-[1240px] mx-auto space-y-6 sm:space-y-8" data-testid="dashboard-new">
      {/* Demo-mode indicator + toggle. Only visible when the localStorage
          flag is on; production users never see this. */}
      {demoMode && (
        <div
          className="flex items-center justify-between gap-3 rounded-xl border border-amber/40 bg-amber/10 px-4 py-2 text-xs text-foreground"
          data-testid="demo-mode-banner"
        >
          <span><strong>Demo mode</strong> · Dashboard is showing fixture data for preview only. Backend / DB are untouched.</span>
          <Button size="sm" variant="ghost" onClick={() => { setDemoEnabled(false); setDemo(false); }} data-testid="demo-mode-exit">
            Exit demo
          </Button>
        </div>
      )}

      {/* Phase 84.22 — Demo data banner. Shown only when the user has
          seeded the Priya-from-Dubai persona. Silent otherwise. */}
      <DemoDataBanner />

      {/* Ask which currency to report in, rather than defaulting to one.
          This shell — not the legacy Dashboard.js — is what Preview and
          Production actually render (lib/uiVariant.js DEFAULT_VARIANT is
          'new'), so a prompt added only to the legacy page reaches nobody. */}
      {!profile?.base_currency && (
        <ReportingCurrencyPrompt
          profile={profile}
          token={session?.access_token}
          onSaved={() => { refreshProfile?.(); reload?.(); }}
        />
      )}

      {/* v3.7.15 Phase 84.32 Part 1 — Compact header replaces the
          full-width ActivationChecklist + RecordManualTxnCard that
          previously ate the top ~450px of the scroll. Getting Started
          + Record transaction are now top-right controls that open a
          drawer + modal respectively. */}
      <DashboardHeader
        greetingName={greetingName}
        subtitle={(snapshot?.balance_sheet?.accounts_count || 0) > 0
          ? undefined
          : 'Link an account or import a statement to unlock personalisation.'}
        activation={{
          hasAccount:     (snapshot?.balance_sheet?.accounts_count || 0) > 0,
          hasImported:    !!snapshot?.flags?.has_transactions,
          hasCategorised: (breakdownCategories || []).some((c) => (
            (c?.base_amount || 0) > 0
            && c?.name
            && c.name !== 'Uncategorised'
            && c.name !== 'Miscellaneous'
          )),
          hasGoal:        !!snapshot?.flags?.has_active_goals,
          hasReflection:  !!latestReflection,
        }}
        onOpenChecklist={() => setChecklistOpen(true)}
        onOpenRecord={() => setRecordOpen(true)}
      />

      {/* Row 1: full-width tabbed Briefing hero. Each briefing update
          contains its OWN Next Best Action, so there is no separate
          "One Thing Worth Doing" card on the Overview — that content
          has been merged into the hero (see DASHBOARD_DATA_CONTRACT.md
          \u00a72.1 revised). */}
      <FinancialBriefingHero
        briefing={briefing}
        keyMetrics={briefingKeyMetrics}
        loading={loading}
        baseCurrency={baseCurrency}
        greetingName={greetingName}
        onOpenDrawer={openDrawer}
        onNavigate={(t) => navigate(t)}
        accountsIncluded={snapshot?.balance_sheet?.accounts_count || 0}
        activationSignals={{
          hasAccount: (snapshot?.balance_sheet?.accounts_count || 0) > 0,
          hasImported: !!snapshot?.flags?.has_transactions,
          hasCategorised: (breakdownCategories || []).some((c) => (
            (c?.base_amount || 0) > 0
            && c?.name
            && c.name !== 'Uncategorised'
            && c.name !== 'Miscellaneous'
          )),
          hasGoal: !!snapshot?.flags?.has_active_goals,
          hasReflection: !!latestReflection,
        }}
      />

      {/* Phase 86.E — whichever debt needs paying next. Silent unless a
          payment is overdue or due within a week. */}
      <DebtDueNudge />

      {/* Row 2: Position Cards (full width). Each card has a per-card
          "link" affordance that opens a bound drawer via onOpenDrawer. */}
      <PositionCards
        loading={loading}
        baseCurrency={baseCurrency}
        spending={positionInputs.spending}
        moneyKept={positionInputs.moneyKept}
        committed={{ ...positionInputs.committed, pct_of_income: committedInputs.pctOfIncome }}
        goal={positionInputs.goal}
        onOpenDrawer={openDrawer}
      />

      {/* Row 3: Attention (wide) + Committed Money. */}
      <div className="grid gap-4 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <NeedsYourAttention
            items={attentionItems}
            loading={loading}
            onOpenDrawer={openDrawer}
            onNavigate={(t) => navigate(t)}
          />
        </div>
        <div className="lg:col-span-1">
          <CommittedMoney
            {...committedInputs}
            categories={demoMode ? DEMO_COMMITTED_CATEGORIES : null}
            reviewNote={demoMode ? DEMO_COMMITTED_NOTE : null}
            loading={loading}
            baseCurrency={baseCurrency}
            onOpenDrawer={openDrawer}
          />
        </div>
      </div>

      {/* Row 4: Money Movement (wide) + Goal Progress. */}
      <div className="grid gap-4 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <MoneyMovement data={demoMode ? DEMO_TREND_12M : trend6m} loading={loading} baseCurrency={baseCurrency} excluded={demoMode ? null : trend6mExcluded} />
        </div>
        <div className="lg:col-span-1">
          <GoalProgress
            {...goalInputs}
            loading={loading}
            baseCurrency={baseCurrency}
            onOpenDrawer={openDrawer}
          />
        </div>
      </div>

      {/* Row 5: Monthly Reflection (full width). Phase 83.4 wires
          `priorMonthRecap` and `opportunity` from the HYBRID briefing
          (rising + opportunity tabs). Previously these were demo-only. */}
      <MonthlyReflection
        question={reflectionQuestion}
        latestAnswer={latestReflection}
        priorMonthRecap={priorMonthRecap}
        opportunity={biggestOpportunity}
        loading={loading}
        onOpenDrawer={openDrawer}
      />

      {error && (
        <div className="text-sm text-destructive" data-testid="dashboard-error">
          Some sections couldn&apos;t load. <button onClick={() => window.location.reload()} className="underline">Retry</button>
        </div>
      )}

      <DetailDrawer
        open={!!drawerType}
        type={drawerType}
        data={drawerData}
        baseCurrency={baseCurrency}
        monthlyContributions={goalContribs}
        onOpenChange={closeDrawer}
      />

      {/* v3.7.15 Phase 84.32 Part 1 — Header-driven surfaces. */}
      <GettingStartedDrawer
        open={checklistOpen}
        onOpenChange={setChecklistOpen}
        activation={{
          hasAccount:     (snapshot?.balance_sheet?.accounts_count || 0) > 0,
          hasImported:    !!snapshot?.flags?.has_transactions,
          hasCategorised: (breakdownCategories || []).some((c) => (
            (c?.base_amount || 0) > 0
            && c?.name
            && c.name !== 'Uncategorised'
            && c.name !== 'Miscellaneous'
          )),
          hasGoal:        !!snapshot?.flags?.has_active_goals,
          hasReflection:  !!latestReflection,
        }}
      />
      <RecordTransactionModal
        open={recordOpen}
        onOpenChange={setRecordOpen}
        accounts={dashAccounts}
        txns={dashTxns}
        onSaved={() => { /* dashboard auto-refreshes via realtime; keep silent */ }}
      />
    </div>
  );
}
